import BaseMappingsModel from './baseMappingsModel';
import { convertResponseToAddress } from '@/types/mapbox';
import { DEFAULT_ADDRESS_RADIUS } from '@/constants/settings';

const MAPPINGS = {
  adr: 'adr',
  loading_address: 'loading_address',
  loading_address_radius: 'loading_address_radius',
  unloading_address: 'unloading_address',
  unloading_address_radius: 'unloading_address_radius',
  additional_loading_addresses: 'additional_loading_addresses',
  additional_unloading_addresses: 'additional_unloading_addresses',
  excluded_loading_countries: 'excluded_loading_countries',
  excluded_unloading_countries: 'excluded_unloading_countries',
  conics: 'conics',
  company_logo_url: 'company_logo_url',
  created_at: 'created_at',
  loading_from: 'loading_from',
  loading_to: 'loading_to',
  dont_show_without_dimensions: 'dont_show_without_dimensions',
  height_from: 'height_from',
  height_to: 'height_to',
  id: 'id',
  length_from: 'length_from',
  length_to: 'length_to',
  only_by_load: 'only_by_load',
  only_by_unload: 'only_by_unload',
  output_by_id: 'output_by_id',
  round_trip: 'round_trip',
  time_type_id: 'time_type_id',
  transport_bodytype_ids: 'transport_bodytype_ids',
  user_id: 'user_id',
  transportation_type_id: 'transportation_type_id',
  volume_from: 'volume_from',
  volume_to: 'volume_to',
  weight_from: 'weight_from',
  weight_to: 'weight_to',
  width_from: 'width_from',
  width_to: 'width_to',
  payment_types_ids: 'payment_types_ids',
  template_title: 'template_title',
  loading_type_ids: 'loading_type_ids',
  package_id: 'package_id',
  package_ids: 'package_ids',
};

class FreightSearch extends BaseMappingsModel {
  static initialValues() {
    const values = {};

    for (let key in MAPPINGS) {
      if (['loading_address', 'unloading_address'].includes(key)) {
        values[key] = null;
      } else if (
        [
          'additional_loading_addresses',
          'additional_unloading_addresses',
          'excluded_loading_countries',
          'excluded_unloading_countries',
        ].includes(key)
      ) {
        values[key] = [];
      } else if (['loading_address_radius', 'unloading_address_radius'].includes(key)) {
        values[key] = DEFAULT_ADDRESS_RADIUS;
      } else {
        values[key] = '';
      }
    }

    return values;
  }

  static clearData = (data) => {
    const values = {};

    for (let key in MAPPINGS) {
      if (data[key] !== '' && key !== 'id' && data[key] !== null && key !== 'created_at' && key !== 'user_id') {
        values[key] = data[key];
      }
    }

    return values;
  };

  static formData(data) {
    const values = this.clearData(data);

    // Helper to ensure string fields are never null
    const normalizeAddress = (addr) => ({
      country_code: addr.country_code || '',
      full_address: addr.full_address || '',
      location: addr.location || {
        latitude: Number(addr.latitude) || 0,
        longitude: Number(addr.longitude) || 0,
      },
      country: addr.country || '',
      state_province: addr.state_province || '',
      city: addr.city || '',
      street: addr.street || '',
      post_code: addr.post_code || '',
    });

    // Handle loading address - from API template (plural) or existing form data (singular)
    if (data.loading_addresses && Array.isArray(data.loading_addresses) && data.loading_addresses.length > 0) {
      // From API template - convert first address to form format (main address)
      const addr = data.loading_addresses[0];
      values.loading_address = {
        value: normalizeAddress(addr),
        label: addr.full_address || '',
      };
      // Additional addresses (all except first)
      if (data.loading_addresses.length > 1) {
        values.additional_loading_addresses = data.loading_addresses.slice(1).map((a, index) => ({
          id: Date.now() + index,
          address: {
            value: normalizeAddress(a),
            label: a.full_address || '',
          },
        }));
      }
    } else if (data.loading_address) {
      values.loading_address = data.loading_address;
    }

    // Handle unloading address - from API template (plural) or existing form data (singular)
    if (data.unloading_addresses && Array.isArray(data.unloading_addresses) && data.unloading_addresses.length > 0) {
      // From API template - convert first address to form format (main address)
      const addr = data.unloading_addresses[0];
      values.unloading_address = {
        value: normalizeAddress(addr),
        label: addr.full_address || '',
      };
      // Additional addresses (all except first)
      if (data.unloading_addresses.length > 1) {
        values.additional_unloading_addresses = data.unloading_addresses.slice(1).map((a, index) => ({
          id: Date.now() + index + 1000, // offset to avoid id collision
          address: {
            value: normalizeAddress(a),
            label: a.full_address || '',
          },
        }));
      }
    } else if (data.unloading_address) {
      values.unloading_address = data.unloading_address;
    }

    if (data.cargo_template_payment_types && data.cargo_template_payment_types.length > 0) {
      values.payment_types_ids = data.cargo_template_payment_types.map((item) => item.payment_type_id);
    }

    // Handle loading_types from template
    if (data.cargo_template_loading_types && data.cargo_template_loading_types.length > 0) {
      // Handle both formats: array of objects { loading_type_id } or array of UUIDs
      values.loading_type_ids = data.cargo_template_loading_types.map((item) =>
        typeof item === 'string' ? item : item.loading_type_id,
      );
    }

    // Handle packages from template
    if (data.cargo_template_packages && data.cargo_template_packages.length > 0) {
      // Handle both formats: array of objects { package_id } or array of UUIDs
      values.package_ids = data.cargo_template_packages.map((item) =>
        typeof item === 'string' ? item : item.package_id,
      );
    }

    // Handle dates - convert ISO strings to yyyy-MM-dd format for form (local timezone)
    if (data.loading_from) {
      const date = new Date(data.loading_from);
      // Use local date to avoid timezone shift
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      values.loading_from = `${year}-${month}-${day}`;
    }
    if (data.loading_to) {
      const date = new Date(data.loading_to);
      // Use local date to avoid timezone shift
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      values.loading_to = `${year}-${month}-${day}`;
    }

    // Handle checkbox fields - preserve null, convert to boolean only if not null
    if (data.adr !== undefined) values.adr = data.adr === null ? null : Boolean(data.adr);
    if (data.conics !== undefined) values.conics = data.conics === null ? null : Boolean(data.conics);
    if (data.round_trip !== undefined) values.round_trip = data.round_trip === null ? null : Boolean(data.round_trip);

    // Handle radius
    if (data.loading_address_radius) values.loading_address_radius = data.loading_address_radius;
    if (data.unloading_address_radius) values.unloading_address_radius = data.unloading_address_radius;

    // Handle excluded countries from template (stored as excluded_loading_addresses)
    if (data.excluded_loading_addresses && Array.isArray(data.excluded_loading_addresses)) {
      values.excluded_loading_countries = data.excluded_loading_addresses.map((addr) => ({
        value: {
          country_code: addr.country_code || '',
          country: addr.country || '',
          full_address: addr.full_address || addr.country || '',
          location: addr.location || {
            longitude: Number(addr.longitude) || 0,
            latitude: Number(addr.latitude) || 0,
          },
        },
        label: addr.country || addr.full_address || '',
      }));
    }
    if (data.excluded_unloading_addresses && Array.isArray(data.excluded_unloading_addresses)) {
      values.excluded_unloading_countries = data.excluded_unloading_addresses.map((addr) => ({
        value: {
          country_code: addr.country_code || '',
          country: addr.country || '',
          full_address: addr.full_address || addr.country || '',
          location: addr.location || {
            longitude: Number(addr.longitude) || 0,
            latitude: Number(addr.latitude) || 0,
          },
        },
        label: addr.country || addr.full_address || '',
      }));
    }

    // Handle only_by_load
    if (data.only_by_load !== undefined && data.only_by_load !== null && data.only_by_load !== '') {
      values.only_by_load = String(data.only_by_load);
    }

    // Handle package_id
    if (data.package_id) values.package_id = data.package_id;

    // Handle transport_bodytype_ids from template
    if (data.cargo_template_transport_bodytypes && data.cargo_template_transport_bodytypes.length > 0) {
      values.transport_bodytype_ids = data.cargo_template_transport_bodytypes;
    } else if (data.transport_bodytype_ids) {
      values.transport_bodytype_ids = data.transport_bodytype_ids;
    }

    if (data.notification_sound_enabled !== undefined) {
      values.notification_sound_enabled = data.notification_sound_enabled;
    }

    if (data.notifications_enabled !== undefined) {
      values.notifications_enabled = data.notifications_enabled;
    }

    delete values.template_title;

    return values;
  }

  // Prepare data for updating/creating a template - includes all search parameters
  static updateTemplateData(data) {
    const loadingAddress = data.loading_address?.value || data.loading_address;
    const unloadingAddress = data.unloading_address?.value || data.unloading_address;

    // Generate template title from addresses
    const fromCity = loadingAddress?.city || loadingAddress?.country || '';
    const toCity = unloadingAddress?.city || unloadingAddress?.country || '';
    const templateTitle = fromCity && toCity ? `${fromCity} → ${toCity}` : fromCity || toCity || 'Новый маршрут';

    const templateData = {
      template_title: templateTitle,
    };

    // Helper to clean address object - remove empty string values
    const cleanAddress = (addr) => {
      if (!addr) return null;
      const cleaned = {};
      for (const [key, value] of Object.entries(addr)) {
        if (value !== '' && value !== null && value !== undefined) {
          if (key === 'location' && typeof value === 'object') {
            // Keep location object as-is if it has valid coordinates
            if (value.latitude !== undefined || value.longitude !== undefined) {
              cleaned[key] = value;
            }
          } else {
            cleaned[key] = value;
          }
        }
      }
      return Object.keys(cleaned).length > 0 ? cleaned : null;
    };

    // Add addresses (main + additional)
    const cleanedLoading = cleanAddress(loadingAddress);
    const cleanedUnloading = cleanAddress(unloadingAddress);

    // Start with main address
    const loadingAddresses = cleanedLoading ? [cleanedLoading] : [];
    const unloadingAddresses = cleanedUnloading ? [cleanedUnloading] : [];

    // Add additional addresses
    if (data.additional_loading_addresses && data.additional_loading_addresses.length > 0) {
      const additionalAddrs = data.additional_loading_addresses
        .filter((item) => item.address?.value)
        .map((item) => cleanAddress(item.address.value))
        .filter(Boolean);
      loadingAddresses.push(...additionalAddrs);
    }
    if (data.additional_unloading_addresses && data.additional_unloading_addresses.length > 0) {
      const additionalAddrs = data.additional_unloading_addresses
        .filter((item) => item.address?.value)
        .map((item) => cleanAddress(item.address.value))
        .filter(Boolean);
      unloadingAddresses.push(...additionalAddrs);
    }

    if (loadingAddresses.length > 0) {
      templateData.loading_addresses = loadingAddresses;
    }
    if (unloadingAddresses.length > 0) {
      templateData.unloading_addresses = unloadingAddresses;
    }

    // Add radius
    if (data.loading_address_radius) {
      templateData.loading_address_radius = Number(data.loading_address_radius);
    }
    if (data.unloading_address_radius) {
      templateData.unloading_address_radius = Number(data.unloading_address_radius);
    }

    // Add dates
    if (data.loading_from) {
      const date = new Date(data.loading_from);
      date.setHours(0, 0, 0, 0);
      templateData.loading_from = date.toISOString();
    }
    if (data.loading_to) {
      const date = new Date(data.loading_to);
      date.setHours(23, 59, 59, 0);
      templateData.loading_to = date.toISOString();
    }

    // Add checkboxes
    if (data.adr) templateData.adr = Boolean(data.adr);
    if (data.conics) templateData.conics = Boolean(data.conics);
    if (data.round_trip) templateData.round_trip = Boolean(data.round_trip);
    if (data.dont_show_without_dimensions)
      templateData.dont_show_without_dimensions = Boolean(data.dont_show_without_dimensions);

    // Add only_by_load / only_by_unload
    if (data.only_by_load !== undefined && data.only_by_load !== '') {
      templateData.only_by_load =
        data.only_by_load === 'true' ? true : data.only_by_load === 'false' ? false : data.only_by_load;
    }
    if (data.only_by_unload !== undefined && data.only_by_unload !== '') {
      templateData.only_by_unload =
        data.only_by_unload === 'true' ? true : data.only_by_unload === 'false' ? false : data.only_by_unload;
    }

    // Add payment_types_ids
    if (data.payment_types_ids && data.payment_types_ids.length > 0) {
      templateData.payment_types_ids = data.payment_types_ids;
    }

    if (data.loading_type_ids && data.loading_type_ids.length > 0) {
      templateData.loading_type_ids = data.loading_type_ids;
    }

    // Add transport_bodytype_ids
    if (data.transport_bodytype_ids) {
      templateData.transport_bodytype_ids = data.transport_bodytype_ids;
    }

    // Add transportation_type_id
    if (data.transportation_type_id) {
      templateData.transportation_type_id = data.transportation_type_id;
    }

    // Add time_type_id
    if (data.time_type_id) {
      templateData.time_type_id = data.time_type_id;
    }

    // Add package_id (single) or package_ids (multiple)
    if (data.package_ids && data.package_ids.length > 0) {
      templateData.package_ids = data.package_ids;
    } else if (data.package_id) {
      templateData.package_id = data.package_id;
    }

    // Add dimension filters
    if (data.weight_from) templateData.weight_from = Number(data.weight_from);
    if (data.weight_to) templateData.weight_to = Number(data.weight_to);
    if (data.volume_from) templateData.volume_from = Number(data.volume_from);
    if (data.volume_to) templateData.volume_to = Number(data.volume_to);
    if (data.length_from) templateData.length_from = Number(data.length_from);
    if (data.length_to) templateData.length_to = Number(data.length_to);
    if (data.width_from) templateData.width_from = Number(data.width_from);
    if (data.width_to) templateData.width_to = Number(data.width_to);
    if (data.height_from) templateData.height_from = Number(data.height_from);
    if (data.height_to) templateData.height_to = Number(data.height_to);

    // Add excluded addresses (countries)
    if (data.excluded_loading_countries && data.excluded_loading_countries.length > 0) {
      templateData.excluded_loading_addresses = data.excluded_loading_countries.map((country) => ({
        country_code: country.value?.country_code || country.country_code || '',
        country: country.value?.country || country.country || country.label || '',
        full_address: country.value?.full_address || country.value?.country || country.label || '',
        location: country.value?.location || { longitude: 0, latitude: 0 },
      }));
    }
    if (data.excluded_unloading_countries && data.excluded_unloading_countries.length > 0) {
      templateData.excluded_unloading_addresses = data.excluded_unloading_countries.map((country) => ({
        country_code: country.value?.country_code || country.country_code || '',
        country: country.value?.country || country.country || country.label || '',
        full_address: country.value?.full_address || country.value?.country || country.label || '',
        location: country.value?.location || { longitude: 0, latitude: 0 },
      }));
    }

    if (data.notification_sound_enabled !== undefined) {
      templateData.notification_sound_enabled = data.notification_sound_enabled;
    }

    if (data.notifications_enabled !== undefined) {
      templateData.notifications_enabled = data.notifications_enabled;
    }

    return templateData;
  }

  static searchData(data) {
    const values = this.clearData(data);

    delete values.template_title;
    delete values.loading_address;
    delete values.unloading_address;
    // These fields are not accepted by search API
    delete values.package_id;
    // Remove form-specific fields that will be converted to API format
    delete values.additional_loading_addresses;
    delete values.additional_unloading_addresses;
    delete values.excluded_loading_countries;
    delete values.excluded_unloading_countries;

    // Helper to normalize address for search API - only include non-empty fields
    const normalizeAddressForSearch = (addr) => {
      const result = {
        location: addr.location,
      };
      if (addr.country_code) result.country_code = addr.country_code;
      if (addr.full_address) result.full_address = addr.full_address;
      if (addr.country) result.country = addr.country;
      if (addr.state_province) result.state_province = addr.state_province;
      if (addr.city) result.city = addr.city;
      if (addr.street) result.street = addr.street;
      if (addr.post_code) result.post_code = addr.post_code;
      return result;
    };

    // Handle loading addresses - from form (singular) or template (plural array)
    if (data.loading_addresses && Array.isArray(data.loading_addresses)) {
      // From API template - convert and normalize
      values.loading_addresses = data.loading_addresses.map((addr) => {
        const converted = convertResponseToAddress(addr);
        return normalizeAddressForSearch(converted);
      });
    } else if (data.loading_address) {
      // From form - convert to array
      const addr =
        'value' in data.loading_address ? data.loading_address.value : convertResponseToAddress(data.loading_address);
      values.loading_addresses = [normalizeAddressForSearch(addr)];
    }

    // Add additional loading addresses
    if (data.additional_loading_addresses && data.additional_loading_addresses.length > 0) {
      const additionalAddrs = data.additional_loading_addresses
        .filter((item) => item.address?.value)
        .map((item) => normalizeAddressForSearch(item.address.value));
      if (!values.loading_addresses) {
        values.loading_addresses = [];
      }
      values.loading_addresses = [...values.loading_addresses, ...additionalAddrs];
    }

    // Handle unloading addresses - from form (singular) or template (plural array)
    if (data.unloading_addresses && Array.isArray(data.unloading_addresses)) {
      // From API template - convert and normalize
      values.unloading_addresses = data.unloading_addresses.map((addr) => {
        const converted = convertResponseToAddress(addr);
        return normalizeAddressForSearch(converted);
      });
    } else if (data.unloading_address) {
      // From form - convert to array
      const addr =
        'value' in data.unloading_address
          ? data.unloading_address.value
          : convertResponseToAddress(data.unloading_address);
      values.unloading_addresses = [normalizeAddressForSearch(addr)];
    }

    // Add additional unloading addresses
    if (data.additional_unloading_addresses && data.additional_unloading_addresses.length > 0) {
      const additionalAddrs = data.additional_unloading_addresses
        .filter((item) => item.address?.value)
        .map((item) => normalizeAddressForSearch(item.address.value));
      if (!values.unloading_addresses) {
        values.unloading_addresses = [];
      }
      values.unloading_addresses = [...values.unloading_addresses, ...additionalAddrs];
    }

    // Handle excluded countries - convert to addresses format for backend
    if (data.excluded_loading_countries && data.excluded_loading_countries.length > 0) {
      values.excluded_loading_addresses = data.excluded_loading_countries.map((country) => ({
        country_code: country.value?.country_code || country.country_code || '',
        country: country.value?.country || country.country || country.label || '',
        full_address: country.value?.full_address || country.value?.country || country.label || '',
        location: country.value?.location || { longitude: 0, latitude: 0 },
      }));
    }
    if (data.excluded_unloading_countries && data.excluded_unloading_countries.length > 0) {
      values.excluded_unloading_addresses = data.excluded_unloading_countries.map((country) => ({
        country_code: country.value?.country_code || country.country_code || '',
        country: country.value?.country || country.country || country.label || '',
        full_address: country.value?.full_address || country.value?.country || country.label || '',
        location: country.value?.location || { longitude: 0, latitude: 0 },
      }));
    }

    if (data.loading_from) {
      values.loading_from = new Date(data.loading_from);
      values.loading_from.setHours(0);
      values.loading_from.setMinutes(0);
      values.loading_from.setSeconds(0);
      values.loading_from = values.loading_from.toISOString();
    }

    if (data.loading_to) {
      values.loading_to = new Date(data.loading_to);
      values.loading_to.setHours(23);
      values.loading_to.setMinutes(59);
      values.loading_to.setSeconds(59);
      values.loading_to = values.loading_to.toISOString();
    }

    // Filter out null/undefined values from arrays
    if (values.payment_types_ids && Array.isArray(values.payment_types_ids)) {
      values.payment_types_ids = values.payment_types_ids.filter((id) => id != null);
      if (values.payment_types_ids.length === 0) {
        delete values.payment_types_ids;
      }
    }

    return values;
  }

  static templateData(data, isTemplate = false) {
    const values = this.clearData(data);

    if (data.loading_address) {
      values.loading_address = isTemplate ? data.loading_address.value : data.loading_address.value.location;
    }
    if (data.unloading_address) {
      values.unloading_address = isTemplate ? data.unloading_address.value : data.unloading_address.value.location;
    }

    return values;
  }
}

export default FreightSearch;
